import { registerCustomMessageMagicNumber } from '@m1/mavlink';
import { useContext, useEffect, useRef } from 'react';

import * as sheco from './messages';

import { MessageContext } from '@/helpers/MessageProvider';
import { useFCTelemetryHandler, useShecoTelemetryHandler } from '@/hooks/mavlinkTelemetryHandlers';
import { useAddressSelector } from '@/hooks/useMavLinkAddress';
import useMavLinkCommandHandler from '@/hooks/useMavLinkCommandHandler';
import usePingListener from '@/hooks/usePingListener';
import useWaitingRobot from '@/hooks/useWaitingRobot';
import useWaitingRobotTimer from '@/hooks/useWaitingRobotTimer';
import EventEmitter from '@/libs/EventEmitter';

const useOrganizer = (robot) => {
  const address = useAddressSelector(robot.id);
  const { publishCommand } = useContext(MessageContext);
  const stickControl = useRef({ y: 0, z: 0 }); // 수동 제어값

  usePingListener(robot.id);
  useWaitingRobotTimer(robot.id);
  const isWaiting = useWaitingRobot(robot.id);

  useShecoTelemetryHandler(robot);
  useFCTelemetryHandler(robot);
  const { addInterval, clearIntervals } = useMavLinkCommandHandler(robot);

  useEffect(() => {
    if (!robot.isActive) return;
    if (!address) return;
    if (isWaiting) return;

    // MavLink custom messages 정의
    try {
      registerCustomMessageMagicNumber('1101', sheco.UsvData.MAGIC_NUMBER);
      registerCustomMessageMagicNumber('1102', sheco.UsvEmergencyGcsData.MAGIC_NUMBER);
      registerCustomMessageMagicNumber('1104', sheco.UsvOilactGcsData.MAGIC_NUMBER);
    } catch (e) {
      // ignore
    }

    let subscribeToken;
    if (robot.isOwned) {
      publishCommand(robot, 'request/autopilot_version', [[...address]]);
      // prettier-ignore
      addInterval('action/stick_control', () => [[address[0], 0, stickControl.current.y, stickControl.current.z, 0]], 50);

      subscribeToken = EventEmitter.subscribe(`${robot.id}/set/stickControl`, ({ y, z }) => {
        stickControl.current.y = y;
        stickControl.current.z = z;
      });
    }

    addInterval('request/heartbeat', [[]], 1000);
    addInterval('request/message_interval', [[...address, 24, 1000000]], 3000); // GPS_RAW_INT
    addInterval('request/message_interval', [[...address, 26, 1000000]], 3000); // SCALED_IMU
    addInterval('request/message_interval', [[...address, 30, 500000]], 3000); // ATTITUDE
    addInterval('request/message_interval', [[...address, 31, 500000]], 3000); // ATTITUDE_QUATERNION
    addInterval('request/message_interval', [[...address, 32, 500000]], 3000); // LOCAL_POSITION_NED
    addInterval('request/message_interval', [[...address, 33, 500000]], 3000); // GLOBAL_POSITION_INT
    addInterval('request/message_interval', [[...address, 36, 500000]], 3000); // SERVO_OUTPUT_RAW
    addInterval('request/message_interval', [[...address, 74, 200000]], 3000); // VFR_HUD
    addInterval('request/message_interval', [[...address, 87, 1000000]], 3000); // POSITION_TARGET_GLOBAL_INT
    addInterval('request/message_interval', [[...address, 105, 1000000]], 3000); // HIGHRES_IMU
    addInterval('request/message_interval', [[...address, 116, 1000000]], 3000); // SCALED_IMU2
    addInterval('request/message_interval', [[...address, 140, 1000000]], 3000); // ACTUATOR_CONTROL_TARGET
    addInterval('request/message_interval', [[...address, 141, 1000000]], 3000); // ALTITUDE
    addInterval('request/message_interval', [[...address, 331, 1000000]], 3000); // ODOMETRY

    return () => {
      if (subscribeToken) {
        EventEmitter.unsubscribe(subscribeToken);
      }
      clearIntervals();
    };
  }, [robot.isActive, isWaiting, address?.[0], address?.[1]]);
};

export default useOrganizer;
